import { useEffect, useState } from "react";
import api from "../lib/api";
import { PageBody, PageHeader } from "../components/Layout";
import EmptyState from "../components/EmptyState";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Label } from "../components/ui/label";
import {
    Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Badge } from "../components/ui/badge";
import {
    Dialog, DialogTrigger, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "../components/ui/dialog";
import { toast } from "sonner";
import { Plus, ArrowClockwise, Trash, FileArrowUp, CheckCircle, WarningCircle, Spinner } from "@phosphor-icons/react";

const PLATFORMS = ["Reddit", "TikTok", "Instagram", "YouTube", "App Store", "Google Play", "Other"];

const initialForm = {
    platform: "Reddit",
    source_url: "",
    source_title: "",
    notes: "",
    topic_tags: "",
};

function StatusPill({ status }) {
    const map = {
        pending: { cls: "bg-secondary text-muted-foreground", Icon: ArrowClockwise, label: "Pending" },
        collecting: { cls: "bg-primary/10 text-primary", Icon: Spinner, label: "Collecting" },
        completed: { cls: "bg-emerald-500/10 text-emerald-700 dark:text-emerald-400", Icon: CheckCircle, label: "Completed" },
        failed: { cls: "bg-accent/10 text-accent", Icon: WarningCircle, label: "Failed" },
    };
    const { cls, Icon, label } = map[status] || map.pending;
    return (
        <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-sm text-[11px] font-mono ${cls}`}>
            <Icon size={11} weight={status === "collecting" ? "bold" : "duotone"} className={status === "collecting" ? "animate-spin" : ""} />
            {label}
        </span>
    );
}

export default function Sources() {
    const [sources, setSources] = useState([]);
    const [loading, setLoading] = useState(true);
    const [form, setForm] = useState(initialForm);
    const [open, setOpen] = useState(false);
    const [csvOpen, setCsvOpen] = useState(false);
    const [csvFile, setCsvFile] = useState(null);
    const [csvPlatform, setCsvPlatform] = useState("Other");

    const load = async () => {
        try {
            const r = await api.get("/sources");
            setSources(r.data);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
        const t = setInterval(load, 6000);
        return () => clearInterval(t);
    }, []);

    const submit = async (collect = false) => {
        if (!form.source_url.trim()) {
            toast.error("Source URL is required");
            return;
        }
        try {
            const tags = form.topic_tags.split(",").map((s) => s.trim()).filter(Boolean);
            const r = await api.post("/sources", {
                platform: form.platform,
                source_url: form.source_url.trim(),
                source_title: form.source_title.trim(),
                notes: form.notes.trim(),
                topic_tags: tags,
            });
            toast.success("Source saved");
            setForm(initialForm);
            setOpen(false);
            await load();
            if (collect) {
                await triggerCollect(r.data.id);
            }
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Failed to save source");
        }
    };

    const triggerCollect = async (id) => {
        try {
            await api.post(`/sources/${id}/collect`, { max_items: 100 });
            toast.success("Collection started — comments will appear in Raw Signals");
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || "Collection failed to start");
        }
    };

    const removeSource = async (id) => {
        if (!window.confirm("Delete this source? Comments will remain.")) return;
        await api.delete(`/sources/${id}`);
        toast.success("Source deleted");
        await load();
    };

    const submitCsv = async () => {
        if (!csvFile) {
            toast.error("Choose a CSV file");
            return;
        }
        const fd = new FormData();
        fd.append("file", csvFile);
        fd.append("platform", csvPlatform);
        try {
            const r = await api.post("/comments/import-csv", fd, {
                headers: { "Content-Type": "multipart/form-data" },
            });
            toast.success(`Imported ${r.data.inserted} signals from CSV`);
            setCsvOpen(false);
            setCsvFile(null);
        } catch (e) {
            toast.error(e?.response?.data?.detail || "CSV import failed");
        }
    };

    return (
        <>
            <PageHeader
                overline="Input Layer"
                title="Sources"
                description="Add public discussions to listen to. Reddit threads work today; TikTok, Instagram, YouTube, and app reviews route through Apify when enabled."
                testid="sources-header"
                actions={
                    <div className="flex gap-2">
                        <Dialog open={csvOpen} onOpenChange={setCsvOpen}>
                            <DialogTrigger asChild>
                                <Button data-testid="open-csv-dialog-btn" variant="outline" size="sm" className="rounded-sm">
                                    <FileArrowUp size={14} weight="duotone" className="mr-1.5" />
                                    CSV (backup)
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm" data-testid="csv-dialog">
                                <DialogHeader>
                                    <DialogTitle className="font-display">Import comments from CSV</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-3">
                                    <div>
                                        <Label className="text-xs">Platform</Label>
                                        <Select value={csvPlatform} onValueChange={setCsvPlatform}>
                                            <SelectTrigger data-testid="csv-platform-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {PLATFORMS.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs">CSV file</Label>
                                        <Input data-testid="csv-file-input" type="file" accept=".csv" onChange={(e) => setCsvFile(e.target.files?.[0] || null)} className="rounded-sm" />
                                    </div>
                                    <p className="text-[11px] text-muted-foreground leading-relaxed">
                                        Required column: <code className="font-mono">raw_text</code>. Optional:
                                        <code className="font-mono"> author_handle, like_count, reply_count, posted_at, topic_tags (semicolon-separated)</code>.
                                    </p>
                                </div>
                                <DialogFooter>
                                    <Button data-testid="csv-import-submit" onClick={submitCsv} className="rounded-sm">Import</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>

                        <Dialog open={open} onOpenChange={setOpen}>
                            <DialogTrigger asChild>
                                <Button data-testid="add-source-btn" size="sm" className="rounded-sm">
                                    <Plus size={14} weight="bold" className="mr-1.5" />
                                    Add source
                                </Button>
                            </DialogTrigger>
                            <DialogContent className="rounded-sm max-w-lg" data-testid="add-source-dialog">
                                <DialogHeader>
                                    <DialogTitle className="font-display">New source</DialogTitle>
                                </DialogHeader>
                                <div className="space-y-3">
                                    <div>
                                        <Label className="text-xs">Platform</Label>
                                        <Select value={form.platform} onValueChange={(v) => setForm({ ...form, platform: v })}>
                                            <SelectTrigger data-testid="source-platform-select" className="rounded-sm"><SelectValue /></SelectTrigger>
                                            <SelectContent>
                                                {PLATFORMS.map((p) => (<SelectItem key={p} value={p}>{p}</SelectItem>))}
                                            </SelectContent>
                                        </Select>
                                    </div>
                                    <div>
                                        <Label className="text-xs">Source URL</Label>
                                        <Input
                                            data-testid="source-url-input"
                                            value={form.source_url}
                                            onChange={(e) => setForm({ ...form, source_url: e.target.value })}
                                            placeholder="https://www.reddit.com/r/dating_advice/comments/..."
                                            className="rounded-sm font-mono text-xs"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Title (optional)</Label>
                                        <Input
                                            data-testid="source-title-input"
                                            value={form.source_title}
                                            onChange={(e) => setForm({ ...form, source_title: e.target.value })}
                                            className="rounded-sm"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Topic tags (comma-separated)</Label>
                                        <Input
                                            data-testid="source-tags-input"
                                            value={form.topic_tags}
                                            onChange={(e) => setForm({ ...form, topic_tags: e.target.value })}
                                            placeholder="swipe fatigue, intentional dating, ENM"
                                            className="rounded-sm"
                                        />
                                    </div>
                                    <div>
                                        <Label className="text-xs">Notes</Label>
                                        <Textarea
                                            data-testid="source-notes-input"
                                            value={form.notes}
                                            onChange={(e) => setForm({ ...form, notes: e.target.value })}
                                            rows={2}
                                            className="rounded-sm"
                                        />
                                    </div>
                                </div>
                                <DialogFooter>
                                    <Button data-testid="save-source-btn" variant="outline" onClick={() => submit(false)} className="rounded-sm">Save</Button>
                                    <Button data-testid="save-and-collect-btn" onClick={() => submit(true)} className="rounded-sm">Save & Collect</Button>
                                </DialogFooter>
                            </DialogContent>
                        </Dialog>
                    </div>
                }
            />

            <PageBody>
                {loading ? (
                    <div className="text-sm text-muted-foreground">Loading…</div>
                ) : sources.length === 0 ? (
                    <EmptyState
                        title="Add your first source"
                        description="Paste a Reddit thread URL, choose tags, and hit Collect Signals. Comments will land in Raw Signals."
                        testid="sources-empty"
                    />
                ) : (
                    <div data-testid="sources-list" className="border border-border">
                        <div className="grid grid-cols-12 gap-3 px-4 py-3 text-[10px] tracking-[0.18em] uppercase text-muted-foreground border-b border-border bg-surface font-semibold">
                            <div className="col-span-2">Platform</div>
                            <div className="col-span-5">Source</div>
                            <div className="col-span-2">Tags</div>
                            <div className="col-span-1 text-right">Signals</div>
                            <div className="col-span-2 text-right">Actions</div>
                        </div>
                        {sources.map((s) => (
                            <div
                                key={s.id}
                                data-testid={`source-row-${s.id}`}
                                className="grid grid-cols-12 gap-3 px-4 py-4 border-b border-border items-center text-sm hover:bg-surface transition-colors"
                            >
                                <div className="col-span-2 flex items-center gap-2">
                                    <Badge variant="outline" className="rounded-sm font-mono text-[10px] tracking-wider">
                                        {s.platform}
                                    </Badge>
                                    <StatusPill status={s.import_status} />
                                </div>
                                <div className="col-span-5 min-w-0">
                                    <div className="font-medium truncate">{s.source_title || s.source_url}</div>
                                    <a href={s.source_url} target="_blank" rel="noreferrer" className="text-[11px] text-muted-foreground font-mono truncate block hover:text-primary">
                                        {s.source_url}
                                    </a>
                                    {s.last_error && (
                                        <div className="text-[11px] text-accent mt-1 truncate">{s.last_error}</div>
                                    )}
                                </div>
                                <div className="col-span-2 flex flex-wrap gap-1">
                                    {(s.topic_tags || []).slice(0, 3).map((t) => (
                                        <span key={t} className="text-[10px] px-1.5 py-0.5 bg-secondary rounded-sm text-secondary-foreground">{t}</span>
                                    ))}
                                </div>
                                <div className="col-span-1 text-right font-mono tabular-nums">{s.comment_count || 0}</div>
                                <div className="col-span-2 flex justify-end gap-1.5">
                                    <Button
                                        data-testid={`collect-btn-${s.id}`}
                                        size="sm"
                                        variant="outline"
                                        onClick={() => triggerCollect(s.id)}
                                        disabled={s.import_status === "collecting"}
                                        className="rounded-sm h-7 text-xs"
                                    >
                                        {s.import_status === "collecting" ? "…" : "Collect"}
                                    </Button>
                                    <Button
                                        data-testid={`delete-source-${s.id}`}
                                        size="sm"
                                        variant="ghost"
                                        onClick={() => removeSource(s.id)}
                                        className="rounded-sm h-7 w-7 p-0"
                                    >
                                        <Trash size={13} />
                                    </Button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </PageBody>
        </>
    );
}
